import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Field, TextInput, SelectInput } from "@/components/Fields";
import { getDocConfig, putDocConfig, uploadConfigImagen, getConfigIa, getConfigIaModelos, putConfigIa } from "@/lib/prevelis";
import { toast } from "sonner";
import { Building2, Upload, Save, Sparkles } from "lucide-react";

const box = "bg-white border border-slate-200 rounded-xl p-6";

export default function Configuracion() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ia, setIa] = useState(null);
  const [modelos, setModelos] = useState([]);

  useEffect(() => {
    getDocConfig().then((d) => setC({ nombre: "", cif: "", direccion: "", telefono: "", email: "", web: "", ...d }));
    getConfigIa().then(setIa).catch(() => {});
    getConfigIaModelos().then((r) => setModelos(r.modelos || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!c) return <Layout title="Configuración"><div className="text-slate-500">Cargando…</div></Layout>;

  const set = (k, v) => setC((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await putDocConfig(c); toast.success("Configuración guardada"); }
    catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const onImg = async (e, tipo) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { await uploadConfigImagen(f, tipo); toast.success(`${tipo === "logo" ? "Logotipo" : "Firma"} actualizado`); }
    catch { toast.error("No se pudo subir la imagen"); }
  };
  const onModelo = async (val) => {
    const m = modelos.find((x) => `${x.provider}|${x.model}` === val);
    if (!m) return;
    setIa({ provider: m.provider, model: m.model });
    try { await putConfigIa({ provider: m.provider, model: m.model }); toast.success("Modelo de IA actualizado"); }
    catch { toast.error("No se pudo actualizar el modelo"); }
  };

  return (
    <Layout title="Configuración" subtitle="Datos de la consultora (PREVELIS) que se incorporan a los documentos generados">
      <div className="max-w-3xl space-y-6" data-testid="config-consultora">
        <div className={box}>
          <div className="flex items-center gap-2 mb-4"><Building2 className="w-5 h-5 text-blue-700" /><h3 className="font-heading font-semibold text-slate-900 text-lg">Datos de la consultora</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><Field label="Nombre / razón social"><TextInput testId="cfg-nombre" value={c.nombre} onChange={(e) => set("nombre", e.target.value)} /></Field></div>
            <Field label="CIF/NIF"><TextInput testId="cfg-cif" value={c.cif} onChange={(e) => set("cif", e.target.value)} /></Field>
            <Field label="Teléfono"><TextInput testId="cfg-telefono" value={c.telefono} onChange={(e) => set("telefono", e.target.value)} /></Field>
            <div className="sm:col-span-2"><Field label="Dirección"><TextInput testId="cfg-direccion" value={c.direccion} onChange={(e) => set("direccion", e.target.value)} /></Field></div>
            <Field label="Email"><TextInput testId="cfg-email" value={c.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Web"><TextInput testId="cfg-web" value={c.web} onChange={(e) => set("web", e.target.value)} /></Field>
          </div>
        </div>
        <div className={box}>
          <h3 className="font-heading font-semibold text-slate-900 text-lg mb-4">Logotipo y firma</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-2 block">Logotipo (portada)</label>
              <label className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50 cursor-pointer">
                <Upload className="w-5 h-5 text-blue-700" /> Subir logotipo
                <input type="file" accept="image/*" className="hidden" data-testid="cfg-logo" onChange={(e) => onImg(e, "logo")} />
              </label>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-2 block">Firma (aprobación)</label>
              <label className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50 cursor-pointer">
                <Upload className="w-5 h-5 text-blue-700" /> Subir firma
                <input type="file" accept="image/*" className="hidden" data-testid="cfg-firma" onChange={(e) => onImg(e, "firma")} />
              </label>
            </div>
          </div>
        </div>
        <div className={box}>
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-blue-700" /><h3 className="font-heading font-semibold text-slate-900 text-lg">Inteligencia artificial</h3></div>
          <p className="text-xs text-slate-500 mb-4">Modelo multimodal usado en el análisis del plan. Puedes cambiarlo cuando quieras.</p>
          <Field label="Modelo de análisis">
            <SelectInput
              testId="cfg-ia-modelo"
              value={ia ? `${ia.provider}|${ia.model}` : ""}
              onChange={(e) => onModelo(e.target.value)}
              options={modelos.map((m) => ({ value: `${m.provider}|${m.model}`, label: m.label }))}
              placeholder="Seleccionar modelo…"
            />
          </Field>
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="cfg-guardar" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-60">
            <Save className="w-5 h-5" /> {saving ? "Guardando…" : "Guardar configuración"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
